import type { NonExceedanceDeclaration, NonExceedanceStatementData } from '@avisoma/shared';
import { PageIdsCell, presentPmaDerScalarEntries } from './PmaDerTables';
import { Td, Th, TruncatedTd } from './cells';
import { extractionRowClass, extractionTableClass, extractionTbodyClass, extractionTheadClass } from './tableClasses';

type DeclarationRow = NonExceedanceDeclaration & { pageIds?: string[] };

/**
 * Non-exceedance statement (DR-6373): the letter scalars a PMA/DER statement carries, plus the
 * aircraft the letter names. Rows carry `pageIds` (plural) after consolidation.
 */
export function presentNonExceedanceScalarEntries(
  statement: NonExceedanceStatementData | null | undefined,
): ReadonlyArray<readonly [string, string]> {
  if (!statement) return [];
  const entries = [...presentPmaDerScalarEntries(statement)];
  if (statement.aircraftRegistration) entries.push(['Aircraft Registration', statement.aircraftRegistration]);
  if (statement.aircraftMsn) entries.push(['Aircraft MSN', statement.aircraftMsn]);
  return entries;
}

export function NonExceedanceDeclarationsTable({
  rows,
  pageIdToPosition,
  onInspect,
}: {
  rows: DeclarationRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: DeclarationRow) => void;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Finding</Th>
          <Th>Quote</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.finding}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row)}
              className={extractionRowClass}
            >
              {pageIdToPosition && (
                <Td>
                  <PageIdsCell pageIds={pageIds} pageIdToPosition={pageIdToPosition} />
                </Td>
              )}
              <Td>{formatFinding(row.finding)}</Td>
              <TruncatedTd value={row.quote} maxWidth="max-w-[480px]" />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function formatFinding(finding: NonExceedanceDeclaration['finding']): string {
  if (finding === 'declared_absent') return 'Absent';
  if (finding === 'present') return 'Present';
  if (finding === 'deferred') return 'Deferred';
  if (finding === 'unclear') return 'Unclear';
  return '—';
}
